import formatToAPIDate from "@/utils/formatToAPIDate";
import differenceInDays from "date-fns/differenceInDays";
import differenceInCalendarMonths from "date-fns/differenceInCalendarMonths";
import { DATA_API_SERVICE } from "@/appConfig";
import ReportingMetrics from "@/classes/reportingMetrics";
import { EntityType } from "@/classes/entity";
import API from "@/api/api";
import AdminReport, { AdminReportResponse } from "@/classes/adminReport";
import { gql } from "@apollo/client/core";
import apolloClient from "@/api/charge";

export type ReportResult = {
  start: string;
  metricValue: number;
  end: string;
};

type Grouping = "HOUR" | "DAY" | "MONTH" | "YEAR" | "AUTO";
type IdType = EntityType;

export type ReportDataResponse = {
  energyConsumedKwh: ReportResult[];
  energyDemandKwh: ReportResult[];
  energyUtilizationKwh: ReportResult[];
  revenue: ReportResult[];
  uniqueDrivers: ReportResult[];
  chargeSessions: ReportResult[];
};

async function getAdminReport(days: number) {
  const response = await API.get<AdminReportResponse>(`${DATA_API_SERVICE}/report/days/${days}`, {
    cache: {
      ignoreCache: true
    }
  });
  return AdminReport.fromAPI(response.data);
}

async function query(start: Date, end: Date, id: string, idType: IdType, grouping: Grouping = "AUTO") {
  // Caculate Grouping if AUTO
  let newGrouping = grouping;
  const newStart = start;
  const newEnd = end;
  const dayDiff = differenceInDays(newStart, newEnd);
  const monthDiff = differenceInCalendarMonths(newStart, newEnd);
  if (grouping === "AUTO") {
    if (Math.abs(dayDiff) <= 7) {
      newGrouping = "HOUR";
    } else if (Math.abs(monthDiff) <= 1) {
      newGrouping = "DAY";
    } else if (Math.abs(monthDiff) <= 12) {
      newGrouping = "MONTH";
    } else {
      newGrouping = "YEAR";
    }
  }

  const res = await apolloClient.query({
    query: gql`
      query Report($filter: UsageReportInput!, $dateFilter: DateFilter!) {
        usage(filter: $filter, dateFilter: $dateFilter) {
          energyConsumedKwh {
            metricValue
            start
            end
          }
          energyDemandKwh {
            metricValue
            start
            end
          }
          energyUtilizationKwh {
            metricValue
            start
            end
          }
          revenue {
            metricValue
            start
            end
          }
          uniqueDrivers {
            metricValue
            start
            end
          }
          chargeSessions {
            metricValue
            start
            end
          }
        }
      }
    `,
    variables: {
      filter: {
        grouping: newGrouping,
        uuid: id,
        entityType: idType
      },
      dateFilter: {
        startDate: formatToAPIDate(newStart),
        endDate: formatToAPIDate(newEnd)
      }
    }
  });

  return ReportingMetrics.fromAPI(res.data.usage);
}

const APIReport = {
  getAdminReport,
  query
};

export default APIReport;
